import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { ReactNode } from 'react';

import { Panel, PanelHeader } from '@/components/core/Panel';
import { PanelBody } from '@/components/core/Panel/PanelBody';
import { DialogHost } from '@/dialogs/DialogHost';
import { useTranslation } from '@/hooks/useTranslation';

import ExecutedPublication from './ExecutedPublication';
import { ScheduledJobsGrid } from './ScheduledJobsGrid';

export type PublicationsPageProps = {
    /**
     * Storybook seam so stories can avoid mounting the query-backed
     * scheduled-jobs grid. Production leaves this unset.
     */
    scheduledJobs?: ReactNode;
    /**
     * Storybook seam so stories can avoid mounting the query-backed
     * executed-publications grid. Production leaves this unset.
     */
    executedPublication?: ReactNode;
};

/**
 * LAX-10148: React shell for `/publications`. Composes the scheduled-jobs
 * grid (LAX-10151) and the existing {@link ExecutedPublication} grid.
 *
 * Icon is a temporary Heroicon stand-in for `syncons-publish` until Anna
 * Kindermann provides the final glyph.
 *
 * `window.reactPages['PublicationsPage']` is exposed via a lazy getter in
 * `main.tsx` only when `NEXT_PUBLICATIONS` is enabled. The AngularJS
 * `/publications` route is unchanged until LAX-10154; with the flag off
 * there is no visible change.
 */
const PublicationsPage = ({ scheduledJobs, executedPublication }: PublicationsPageProps) => {
    const { t } = useTranslation();

    return (
        <DialogHost>
            <Panel>
                <PanelHeader
                    icon={PaperAirplaneIcon}
                    title={t('PUBLICATIONS')}
                    className="tw-mb-8"
                />
                <PanelBody>
                    {scheduledJobs ?? <ScheduledJobsGrid />}
                    {executedPublication ?? <ExecutedPublication />}
                </PanelBody>
            </Panel>
        </DialogHost>
    );
};

PublicationsPage.displayName = 'PublicationsPage';

export { PublicationsPage };
